import React from 'react';
import { Layout } from '../components/layout/Layout';
import { useLanguage } from '../context/LanguageContext';
import { Target, Users, Shield, BookOpen, Clock, CheckCircle } from 'lucide-react';

const DIPLOMATIC_IMG = "https://images.unsplash.com/photo-1718638779329-0994ce1bafc5?crop=entropy&cs=srgb&fm=jpg&q=85";

const AboutPage = () => {
  const { t } = useLanguage();

  const sections = [
    { icon: Target, key: 'vision', color: 'oar-red' },
    { icon: Users, key: 'council', color: 'oar-gold' },
    { icon: Shield, key: 'governance', color: 'oar-green' },
    { icon: BookOpen, key: 'committee', color: 'oar-gold' },
  ];

  const objectives = [
    t('about.objectives.obj1'),
    t('about.objectives.obj2'),
    t('about.objectives.obj3'),
    t('about.objectives.obj4'),
    t('about.objectives.obj5'),
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-32 md:py-40 bg-oar-blue-dark overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={DIPLOMATIC_IMG}
            alt="Diplomatic Conference"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-oar-blue-dark via-oar-blue-dark/90 to-oar-blue-dark" />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <span className="text-xs md:text-sm uppercase tracking-[0.2em] text-oar-orange font-bold">
            À Propos
          </span>
          <h1
            data-testid="about-title"
            className="font-heading text-4xl md:text-6xl font-bold tracking-tight text-white mt-4 mb-6"
          >
            {t('about.title')}
          </h1>
          <p className="max-w-2xl text-lg text-gray-300">
            {t('about.subtitle')}
          </p>
        </div>
      </section>

      {/* History Section */}
      <section className="py-24 bg-oar-bg-secondary">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <Clock className="w-6 h-6 text-oar-orange" />
                <span className="text-xs uppercase tracking-[0.2em] text-oar-orange font-bold">
                  Fondation 2022
                </span>
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-oar-text-primary mb-6">
                {t('about.history.title')}
              </h2>
              <p className="text-oar-text-secondary leading-relaxed">
                {t('about.history.text')}
              </p>
            </div>
            <div className="bg-oar-blue-dark border border-oar-blue/30 p-8 rounded-sm">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-3 h-3 bg-oar-red rounded-full mt-2" />
                  <div>
                    <div className="text-oar-orange font-semibold">Août 2022</div>
                    <div className="text-gray-300 text-sm">Création de l'OAR à Bangui</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-3 h-3 bg-oar-gold rounded-full mt-2" />
                  <div>
                    <div className="text-oar-orange font-semibold">2023</div>
                    <div className="text-gray-300 text-sm">Expansion vers 10 pays africains</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-3 h-3 bg-oar-green rounded-full mt-2" />
                  <div>
                    <div className="text-oar-orange font-semibold">2024</div>
                    <div className="text-gray-300 text-sm">Premier Forum Afrique-Russophonie</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-3 h-3 bg-oar-gold rounded-full mt-2 opacity-50" />
                  <div>
                    <div className="text-oar-orange font-semibold">2040</div>
                    <div className="text-gray-300 text-sm">Objectif: 50 pays membres</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Sections Grid */}
      <section className="py-24 bg-oar-blue-dark">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-8">
            {sections.map((section, index) => (
              <div
                key={index}
                data-testid={`about-section-${section.key}`}
                className="bg-oar-blue border border-oar-blue-light/30 p-8 md:p-10 rounded-sm card-hover"
              >
                <div
                  className={`w-12 h-12 flex items-center justify-center rounded-sm mb-6 ${
                    section.color === 'oar-red'
                      ? 'bg-oar-red/20 text-oar-red-light'
                      : section.color === 'oar-gold'
                      ? 'bg-oar-orange/20 text-oar-orange'
                      : 'bg-oar-green/20 text-oar-green-light'
                  }`}
                >
                  <section.icon className="w-6 h-6" />
                </div>
                <h3 className="font-heading text-xl md:text-2xl font-medium text-oar-orange mb-4">
                  {t(`about.${section.key}.title`)}
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  {t(`about.${section.key}.text`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Objectives Section */}
      <section className="py-24 bg-oar-bg-secondary">
        <div className="gold-line" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16">
          <div className="text-center mb-12">
            <span className="text-xs md:text-sm uppercase tracking-[0.2em] text-oar-orange font-bold">
              Vision 2030
            </span>
            <h2
              data-testid="objectives-title"
              className="font-heading text-3xl md:text-4xl font-semibold text-oar-text-primary mt-4"
            >
              {t('about.objectives.title')}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {objectives.map((obj, index) => (
              <div
                key={index}
                className="flex items-center gap-4 bg-oar-blue-dark border border-oar-blue/30 p-4 rounded-sm"
              >
                <CheckCircle className="w-5 h-5 text-oar-orange flex-shrink-0" />
                <span className="text-white text-sm">{obj}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default AboutPage;
